import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SidebarLayout from './SidebarLayout';
import './shopStyles.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.ogestyle.com';

const NotificationSettings = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const [businessId, setBusinessId] = useState(null);
  
  const [notificationPrefs, setNotificationPrefs] = useState({
    emailNewBookings: true,
    emailDailySummary: false,
    emailLowStock: true,
    emailCancellationRequests: true
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchAlerts();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('ogestyle_token');
    if (!token) return null;
    return { Authorization: `Bearer ${token}` };
  };

  const fetchAlerts = async () => {
    try {
      setAlertsLoading(true);
      const headers = getAuthHeaders();
      if (!headers) {
        setAlerts([]);
        return;
      }

      const res = await axios.get(`${API_BASE_URL}/api/notifications`, {
        headers,
        params: { limit: 50 },
      });

      setAlerts(Array.isArray(res.data?.notifications) ? res.data.notifications : []);
    } catch (err) {
      setAlerts([]);
    } finally {
      setAlertsLoading(false);
    }
  };

  const markAlertAsRead = async (notificationId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      await axios.patch(
        `${API_BASE_URL}/api/notifications/${notificationId}/read`,
        {},
        { headers },
      );

      setAlerts((prev) =>
        prev.map((item) =>
          item._id === notificationId ? { ...item, read: true } : item,
        ),
      );
    } catch (err) {
    }
  };

  const markAllAlertsAsRead = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      await axios.post(
        `${API_BASE_URL}/api/notifications/mark-all-read`,
        {},
        { headers },
      );

      setAlerts((prev) => prev.map((item) => ({ ...item, read: true })));
    } catch (err) {
      showAlert('Failed to mark alerts as read', 'error');
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const openAlert = async (item) => {
    if (!item?.read) {
      await markAlertAsRead(item._id);
    }

    const rawUrl = item?.actionUrl;
    const normalizeActionUrl = (url) => {
      if (!url || typeof url !== 'string') return url;

      if (
        url === '/products' ||
        url === '/dashboard/freelancer/products' ||
        url === '/dashboard/booth-renter/products'
      ) {
        return '/dashboard/products';
      }

      return url;
    };

    const url = normalizeActionUrl(rawUrl);
    if (!url) return;

    if (url.startsWith('/')) {
      navigate(url);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('ogestyle_token');
      
      let busId = null;
      
      try {
        const staffRes = await axios.get(`${API_BASE_URL}/api/staff/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const staffData = staffRes.data.staff || staffRes.data;
        
        if (staffData) {
          busId = typeof staffData.businessId === 'object' 
            ? staffData.businessId?._id 
            : staffData.businessId;
        }
      } catch (error) {
      }
      
      if (!busId) {
        const shopsRes = await axios.get(`${API_BASE_URL}/api/shop/my-shops`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const shops = Array.isArray(shopsRes.data) ? shopsRes.data : (shopsRes.data.shops || []);
        
        if (shops.length > 0) {
          busId = shops[0].businessId?._id || shops[0].businessId;
        }
      }
      
      if (!busId) {
        showAlert('No business associated with your account', 'error');
        setLoading(false);
        return;
      }
      
      setBusinessId(busId);
      
      const businessRes = await axios.get(
        `${API_BASE_URL}/api/businesses/${busId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (businessRes.data.notificationPreferences) {
        setNotificationPrefs(businessRes.data.notificationPreferences);
      }
      
      setLoading(false);
    } catch (err) {
      showAlert('Failed to load settings', 'error');
      setLoading(false);
    }
  };

  const showAlert = (message, type = 'success') => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
  };

  const handleSaveNotifications = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('ogestyle_token');
      
      await axios.put(
        `${API_BASE_URL}/api/businesses/${businessId}/notification-prefs`,
        { notificationPreferences: notificationPrefs },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showAlert('Notification preferences updated successfully', 'success');
      setSaving(false);
    } catch (err) {
      showAlert(err.response?.data?.message || 'Failed to save preferences', 'error');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div className="bookings-container settings-page">
          <div className="loading-state">Loading settings...</div>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="bookings-container settings-page">
        <button
          onClick={() => navigate('/dashboard/settings')}
          style={{
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '8px 16px',
            fontSize: '14px',
            color: '#374151',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '16px',
            transition: 'all 0.2s',
            fontWeight: '500'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#f9fafb';
            e.currentTarget.style.borderColor = '#d1d5db';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'white';
            e.currentTarget.style.borderColor = '#e5e7eb';
          }}
        >
          <span>←</span>
          <span>Back to Settings</span>
        </button>

        {alert.show && (
          <div className={`alert alert-${alert.type}`}>
            {alert.message}
          </div>
        )}

            <div className="settings-section" style={{ marginBottom: '20px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'flex-start' : 'center',
                flexDirection: isMobile ? 'column' : 'row',
                gap: '10px',
                marginBottom: '12px'
              }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: isMobile ? '18px' : '20px' }}>In-App Alerts</h3>
                  <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '13px' }}>
                    All in-app notifications for your account.
                  </p>
                </div>
                {alerts.filter((n) => !n.read).length > 0 && (
                  <button
                    type="button"
                    onClick={markAllAlertsAsRead}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      background: 'white',
                      color: '#374151',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: 600,
                    }}
                  >
                    Mark all read
                  </button>
                )}
              </div>

              {alertsLoading ? (
                <div style={{ padding: '12px', color: '#6b7280', fontSize: '14px' }}>
                  Loading alerts...
                </div>
              ) : alerts.length === 0 ? (
                <div style={{ padding: '12px', color: '#6b7280', fontSize: '14px' }}>
                  No in-app alerts yet.
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '10px' }}>
                  {alerts.slice(0, 20).map((item) => (
                    <div
                      key={item._id}
                      onClick={() => openAlert(item)}
                      style={{
                        border: `1px solid ${item.read ? '#e5e7eb' : '#bfdbfe'}`,
                        borderLeft: `4px solid ${item.read ? '#d1d5db' : '#2563eb'}`,
                        borderRadius: '8px',
                        padding: '12px',
                        background: item.read ? '#ffffff' : '#eff6ff',
                        cursor: item.actionUrl ? 'pointer' : 'default',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>
                          {item.title || 'Alert'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                          {formatTimeAgo(item.createdAt)}
                        </div>
                      </div>
                      <div style={{ marginTop: '6px', fontSize: '13px', color: '#374151' }}>
                        {item.message}
                      </div>
                      {!item.read && (
                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#1d4ed8', fontWeight: 700 }}>
                          New
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

        <div className="bookings-header">
          <div>
            <h1 className="bookings-title" style={{ fontSize: isMobile ? '24px' : '28px' }}>Notification Preferences</h1>
            <p className="bookings-subtitle" style={{ fontSize: isMobile ? '14px' : '16px' }}>Choose how you want to be notified</p>
          </div>
        </div>

        <div className="settings-section">
          <div className="notification-prefs">
            <div className="notification-item">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={notificationPrefs.emailNewBookings}
                  onChange={(e) => setNotificationPrefs({ ...notificationPrefs, emailNewBookings: e.target.checked })}
                />
                <div>
                  <span className="notification-title">New Bookings</span>
                  <span className="notification-desc">Get notified when customers make new bookings</span>
                </div>
              </label>
            </div>

            <div className="notification-item">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={notificationPrefs.emailDailySummary}
                  onChange={(e) => setNotificationPrefs({ ...notificationPrefs, emailDailySummary: e.target.checked })}
                />
                <div>
                  <span className="notification-title">Daily Summary</span>
                  <span className="notification-desc">Receive a daily email with booking summary</span>
                </div>
              </label>
            </div>

            <div className="notification-item">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={notificationPrefs.emailLowStock}
                  onChange={(e) => setNotificationPrefs({ ...notificationPrefs, emailLowStock: e.target.checked })}
                />
                <div>
                  <span className="notification-title">Low Stock Alerts</span>
                  <span className="notification-desc">Get alerted when products are running low</span>
                </div>
              </label>
            </div>

            <div className="notification-item">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={notificationPrefs.emailCancellationRequests}
                  onChange={(e) => setNotificationPrefs({ ...notificationPrefs, emailCancellationRequests: e.target.checked })}
                />
                <div>
                  <span className="notification-title">Cancellation Requests</span>
                  <span className="notification-desc">Get notified of pending cancellation requests</span>
                </div>
              </label>
            </div>
          </div>

          <div className="settings-actions" style={{ flexWrap: 'wrap' }}>
            <button
              className="btn btn-primary"
              onClick={handleSaveNotifications}
              disabled={saving}
              style={{ width: isMobile ? '100%' : 'auto' }}
            >
              {saving ? 'Saving...' : 'Save Preferences'}
            </button>
          </div>
        </div>
      </div>
    </SidebarLayout>
  );
};

export default NotificationSettings;
